import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { fmtTime } from "@/lib/livecoach";

export const RespondDialog = ({ appt, onClose, onDone }) => {
  const [pick, setPick] = useState(appt?.proposed_times?.[0] || "");
  const [alt, setAlt] = useState("");
  const [busy, setBusy] = useState(false);

  const act = async (action) => {
    setBusy(true);
    try {
      const body = { action };
      if (action === "accept") body.start_at = pick;
      if (action === "alternate") {
        if (!alt) { toast.error("Pick an alternate date & time"); setBusy(false); return; }
        body.proposed_times = [new Date(alt).toISOString()];
      }
      await api.post(`/livecoach/appointments/${appt.appt_id}/respond`, body);
      toast.success(action === "accept" ? "Session confirmed!" : action === "decline" ? "Declined" : "Alternate time proposed");
      onDone(); onClose();
    } catch (er) { toast.error(formatApiError(er)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!appt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white max-w-md">
        <DialogHeader><DialogTitle className="font-black">Respond to session</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-white/70">{appt?.objective}</div>
          <div>
            <div className="text-xs font-bold text-white/60 mb-1.5">Proposed times</div>
            <div className="space-y-1.5">
              {(appt?.proposed_times || []).map((t) => (
                <label key={t} className={`flex items-center gap-2.5 rounded-lg border p-2.5 cursor-pointer ${pick === t ? "border-emerald-400/60 bg-emerald-400/10" : "border-white/10 bg-white/5"}`}>
                  <input type="radio" name="t" checked={pick === t} onChange={() => setPick(t)} data-testid={`lc-pick-time`} />
                  <span className="text-sm font-semibold">{fmtTime(t)}</span>
                </label>
              ))}
            </div>
          </div>
          <Button className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#04121f] font-black" disabled={busy || !pick} onClick={() => act("accept")} data-testid="lc-accept-btn">Accept selected time</Button>
          <div className="flex items-center gap-2">
            <Input type="datetime-local" value={alt} onChange={(e) => setAlt(e.target.value)} className="bg-white/5 border-white/15 text-white" data-testid="lc-alt-input" />
            <Button variant="outline" className="border-amber-400/40 text-amber-300 hover:bg-amber-400/10 shrink-0" disabled={busy} onClick={() => act("alternate")} data-testid="lc-alt-btn">Propose</Button>
          </div>
          <Button variant="ghost" className="w-full text-red-400/70 hover:text-red-300" disabled={busy} onClick={() => act("decline")} data-testid="lc-decline-btn">Decline</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ThreadDialog = ({ appt, onClose }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const load = () => appt && api.get(`/livecoach/appointments/${appt.appt_id}/messages`).then((r) => setMsgs(r.data.messages)).catch(() => {});
  useEffect(() => { load(); }, [appt?.appt_id]); // eslint-disable-line

  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/livecoach/appointments/${appt.appt_id}/messages`, { text: text.trim() });
      setText(""); load();
    } catch (er) { toast.error(formatApiError(er)); }
  };

  return (
    <Dialog open={!!appt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white max-w-md">
        <DialogHeader><DialogTitle className="font-black">Session thread</DialogTitle></DialogHeader>
        <div className="max-h-72 overflow-y-auto space-y-2 pr-1" data-testid="lc-thread-messages">
          {msgs.length === 0 && <div className="text-xs text-white/40">No messages yet.</div>}
          {msgs.map((m) => (
            <div key={m.msg_id} className="rounded-lg bg-white/5 border border-white/10 p-2.5">
              <div className="text-[10px] font-bold text-white/40">{m.from_name} · {fmtTime(m.at)}</div>
              <div className="text-sm text-white/85 mt-0.5">{m.text}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input placeholder="Message…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} className="bg-white/5 border-white/15 text-white" data-testid="lc-thread-input" />
          <Button onClick={send} className="bg-cyan-500 hover:bg-cyan-400 text-[#04121f] font-black shrink-0" data-testid="lc-thread-send">Send</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
